/**
 * Local Wikipedia-style lore articles.
 * These override (or add) matching entries from the lore API by section + title,
 * except orders which merge by orderId (names/images come from the API).
 */
import asrahArticle from './asrah';
import exolotDominionArticle from './exolotDominion';
import remnantsOfHumanityArticle from './remnantsOfHumanity';
import leagueOfShadowsArticle from './leagueOfShadows';
import rasChildrenArticle from './rasChildren';
import theSentientsArticle from './theSentients';
import mechanicsArticles from './mechanics';
import orderTypeArticles, { mergeOrderTypeLoreArticles } from './orderTypes';
import { mergeOrderLoreArticles } from './orders';
import { parseWikiArticle } from './parseWikiArticle';

/** @type {Record<string, Record<string, { title: string, text: string, format?: string, image?: string }>>} */
export const LORE_ARTICLES = {
  factions: {
    Asrah: asrahArticle,
    'Exolot Dominion': exolotDominionArticle,
    'League of Shadows': leagueOfShadowsArticle,
    "Ra's Children": rasChildrenArticle,
    'Remnants of Humanity': remnantsOfHumanityArticle,
    'The Sentients': theSentientsArticle,
  },
  mechanics: mechanicsArticles,
  // order-type handled by mergeOrderTypeLoreArticles (Coming soon stubs)
};

/**
 * Merge local wiki articles into API lore data.
 * Local articles win on title match within a section; missing titles are appended.
 * Orders are rebuilt from the API order list and local orderId-keyed texts.
 * @param {Record<string, Array>} loreData
 * @returns {Record<string, Array>}
 */
export function mergeLoreArticles(loreData) {
  if (!loreData || typeof loreData !== 'object') {
    throw new Error('mergeLoreArticles: loreData is required');
  }

  const merged = { ...loreData };

  for (const [section, articlesByTitle] of Object.entries(LORE_ARTICLES)) {
    const sectionEntries = Array.isArray(merged[section]) ? [...merged[section]] : [];

    for (const [titleKey, article] of Object.entries(articlesByTitle)) {
      if (!article || !article.title || !article.text) {
        throw new Error(`mergeLoreArticles: incomplete article for ${section}/${titleKey}`);
      }

      const entry = {
        id: `local-wiki-${section}-${article.title}`,
        title: article.title,
        text: article.text,
        format: article.format || 'wiki',
        ...(article.image ? { image: article.image } : {}),
      };

      const existingIndex = sectionEntries.findIndex(
        (e) => (e.title || '').toLowerCase() === article.title.toLowerCase()
      );

      if (existingIndex >= 0) {
        const existing = sectionEntries[existingIndex];
        sectionEntries[existingIndex] = {
          ...existing,
          ...entry,
          // Keep DB id if present so list keys stay stable across reloads
          id: existing.id != null ? existing.id : entry.id,
          image: entry.image || existing.image,
        };
      } else {
        sectionEntries.push(entry);
      }
    }

    merged[section] = sectionEntries;
  }

  // Drop API duplicates that landed in the wrong section (e.g. type "mechanic" → factions)
  const ownedBySection = {};
  for (const [section, articlesByTitle] of Object.entries(LORE_ARTICLES)) {
    for (const article of Object.values(articlesByTitle)) {
      ownedBySection[(article.title || '').toLowerCase()] = section;
    }
  }
  for (const [section, entries] of Object.entries(merged)) {
    if (!Array.isArray(entries) || section === 'orders') continue;
    merged[section] = entries.filter((entry) => {
      const owner = ownedBySection[(entry.title || '').toLowerCase()];
      return !owner || owner === section;
    });
  }

  // Orders: API supplies all orders (en-us name + image); local texts keyed by orderId
  merged.orders = mergeOrderLoreArticles(Array.isArray(merged.orders) ? merged.orders : []);

  // Order types: keep existing titles; replace DB texts with Coming soon / local articles
  merged['order-type'] = mergeOrderTypeLoreArticles(
    Array.isArray(merged['order-type']) ? merged['order-type'] : []
  );

  return merged;
}

export { parseWikiArticle };
export { createComingSoonArticle, createOrderComingSoonArticle } from './createComingSoonArticle';
export { ORDER_LORE_BY_ID, DEFAULT_ORDER_LORE_TEXT, mergeOrderLoreArticles } from './orders';
export {
  DEFAULT_ORDER_TYPE_LORE_TEXT,
  mergeOrderTypeLoreArticles,
} from './orderTypes';
export { orderTypeArticles };
